import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import './HistoryStats.css';

function HistoryStats() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('stats'); // 'stats' or 'calendar'
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadData();
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [historyData, statsData] = await Promise.all([
        apiClient.getHistory(),
        apiClient.getStats(),
      ]);
      setHistory(historyData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(isGerman ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isGerman ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: 'short',
    });
  };

  const getStreakHint = (streakDays) => {
    if (streakDays === 0) return t('stats.streakHints.start');
    if (streakDays === 1) return t('stats.streakHints.keepGoing');
    if (streakDays < 7) return t('stats.streakHints.goodPath');
    if (streakDays < 30) return t('stats.streakHints.impressive');
    return t('stats.streakHints.champion');
  };

  const getWeekdayShort = (index) => {
    const weekdays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    return t(`stats.weekdays.${weekdays[index]}`);
  };

  const getWeekdayFull = (index) => {
    return t(`stats.weekdaysFull.${index}`);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('stats.loading')}</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="dashboard-container history-dashboard">
        <main className="dashboard-main history-main">
        <div className="view-switcher">
          <button
            className={`view-btn ${view === 'stats' ? 'active' : ''}`}
            onClick={() => setView('stats')}
            data-testid="stats-view"
          >
            📊 {t('stats.viewStats')}
          </button>
          <button
            className={`view-btn ${view === 'calendar' ? 'active' : ''}`}
            onClick={() => setView('calendar')}
            data-testid="calendar-view"
          >
            📅 {t('stats.viewCalendar')}
          </button>
        </div>

        {view === 'stats' && stats && (
          <div className="stats-container">
            {/* Streak & Achievements */}
            <div className="wellness-section">
              <div className="streak-card">
                <div className="streak-flame">🔥</div>
                <div className="streak-content">
                  <div className="streak-number">{stats.streak_days}</div>
                  <div className="streak-label">{t('stats.daysStreak')}</div>
                  <div className="streak-hint">
                    {getStreakHint(stats.streak_days)}
                  </div>
                </div>
              </div>

              {stats.achievements && stats.achievements.length > 0 && (
                <div className="achievements-section">
                  <h3 className="achievements-title">🏆 {t('stats.achievements')}</h3>
                  <div className="achievements-grid">
                    {stats.achievements.map((achievement) => (
                      <div key={achievement.id} className="achievement-badge">
                        <div className="achievement-icon">{achievement.icon}</div>
                        <div className="achievement-name">{achievement.name}</div>
                        <div className="achievement-desc">{achievement.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Summary Cards */}
            <div className="stats-summary">
              <div className="summary-card">
                <div className="summary-icon">📈</div>
                <div className="summary-content">
                  <div className="summary-value">{stats.total_activations}</div>
                  <div className="summary-label">{t('stats.totalActivations')}</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">📅</div>
                <div className="summary-content">
                  <div className="summary-value">{stats.this_week}</div>
                  <div className="summary-label">{t('stats.thisWeek')}</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">🗓️</div>
                <div className="summary-content">
                  <div className="summary-value">{stats.this_month}</div>
                  <div className="summary-label">{t('stats.thisMonth')}</div>
                </div>
              </div>
            </div>

            {/* Most Used Frequencies */}
            <div className="stats-section">
              <h2 className="section-title">{t('stats.mostUsedTotal')}</h2>
              <div className="frequency-stats-list">
                {stats.most_used && stats.most_used.length > 0 ? (
                  stats.most_used.map((item, index) => (
                    <div key={index} className="frequency-stat-item">
                      <div className="stat-rank">{index + 1}</div>
                      <div
                        className="stat-icon"
                        style={{ color: item.frequency_color }}
                      >
                        {item.frequency_icon}
                      </div>
                      <div className="stat-info">
                        <div className="stat-name">{item.frequency_name}</div>
                        <div className="stat-count">{item.count}x {t('stats.activated')}</div>
                      </div>
                      <div className="stat-bar">
                        <div
                          className="stat-bar-fill"
                          style={{
                            width: `${(item.count / stats.most_used[0].count) * 100}%`,
                            backgroundColor: item.frequency_color,
                          }}
                        ></div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="empty-message">{t('stats.noDataYet')}</p>
                )}
              </div>
            </div>

            {/* Most Used This Week */}
            <div className="stats-section">
              <h2 className="section-title">{t('stats.top5Week')}</h2>
              <div className="frequency-stats-list compact">
                {stats.most_used_week && stats.most_used_week.length > 0 ? (
                  stats.most_used_week.map((item, index) => (
                    <div key={index} className="frequency-stat-item">
                      <div className="stat-rank">{index + 1}</div>
                      <div
                        className="stat-icon"
                        style={{ color: item.frequency_color }}
                      >
                        {item.frequency_icon}
                      </div>
                      <div className="stat-info">
                        <div className="stat-name">{item.frequency_name}</div>
                        <div className="stat-count">{item.count}x</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="empty-message">{t('stats.noDataWeek')}</p>
                )}
              </div>
            </div>

            {/* Weekday Trends */}
            {stats.weekday_activity && stats.weekday_activity.length > 0 && (
              <div className="stats-section">
                <h2 className="section-title">📅 {t('stats.weekdayPatterns')}</h2>
                <div className="weekday-chart">
                  {[0, 1, 2, 3, 4, 5, 6].map((index) => {
                    const dayData = stats.weekday_activity.find(d => d._id === (index + 1));
                    const count = dayData ? dayData.count : 0;
                    const maxCount = Math.max(...stats.weekday_activity.map(d => d.count));
                    const heightPercent = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    
                    return (
                      <div key={index} className="weekday-bar-container">
                        <div
                          className="weekday-bar"
                          style={{ height: `${heightPercent}%` }}
                          title={`${getWeekdayShort(index)}: ${count} ${t('stats.activations')}`}
                        ></div>
                        <div className="weekday-label">{getWeekdayShort(index)}</div>
                        <div className="weekday-count">{count}</div>
                      </div>
                    );
                  })}
                </div>
                <p className="chart-insight">
                  {stats.weekday_activity.length > 0 && (() => {
                    const sorted = [...stats.weekday_activity].sort((a, b) => b.count - a.count);
                    const topDay = sorted[0];
                    return t('stats.activeOn', { day: getWeekdayFull(topDay._id - 1) });
                  })()}
                </p>
              </div>
            )}

            {/* Daily Activity Chart */}
            {stats.daily_activity && stats.daily_activity.length > 0 && (
              <div className="stats-section">
                <h2 className="section-title">{t('stats.activityLast30')}</h2>
                <div className="activity-chart">
                  {stats.daily_activity.map((day) => (
                    <div key={day._id} className="activity-bar-container">
                      <div
                        className="activity-bar"
                        style={{
                          height: `${Math.min((day.count / 20) * 100, 100)}%`,
                        }}
                        title={`${formatDateShort(day._id)}: ${day.count} ${t('stats.activations')}`}
                      ></div>
                      <div className="activity-label">{formatDateShort(day._id)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'calendar' && (
          <div className="calendar-container">
            <h2 className="section-title">{t('stats.activationHistory')}</h2>
            <div className="history-timeline">
              {history && history.length > 0 ? (
                history.map((entry) => (
                  <div key={entry.id} className="history-entry">
                    <div className="history-date">
                      {formatDate(entry.activated_at)}
                    </div>
                    <div
                      className="history-frequency"
                      style={{ borderLeftColor: entry.frequency_color }}
                    >
                      <span
                        className="history-icon"
                        style={{ color: entry.frequency_color }}
                      >
                        {entry.frequency_icon}
                      </span>
                      <span className="history-name">{entry.frequency_name}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-message">{t('stats.noHistory')}</p>
              )}
            </div>
          </div>
        )}
      </main>
      </div>
    </>
  );
}

export default HistoryStats;
